/* ===========================================================================
   c-lp-shop-by-challenge.js — prev/next, tab chips, progress bar and adaptive
   height for the "Shop by Pet-Home Challenge" scroll-snap track.

   Progressive enhancement only (CONVENTIONS, review round 1, rule 2). Without
   JavaScript the track is a native scroll-snap rail (swipe, trackpad, arrow
   keys on the focused region) at the tallest card's natural height, and every
   control ships with `hidden`. No third-party libraries. Scoped per
   [data-sl-id] root, bound once, re-inits on shopify:section:load.

   Adaptive height: the track is sized to the tallest card that is at least
   half inside its viewport (1 card below 978px, 2 from 978px), so a short
   card is not followed by the empty height of a tall card further along.
   Recomputed after load, on scrollend (debounced scroll where scrollend is
   missing), on resize, when card sizes change, and when focus enters a card
   (that card is always included, so focused content is never clipped).
   =========================================================================== */
(function () {
  'use strict';
  if (window.__cLpShopByChallenge) return;
  window.__cLpShopByChallenge = true;

  var SELECTOR = '[data-sl-id="c-lp-shop-by-challenge"]';
  var SETTLE_MS = 120;

  function init(root) {
    if (!root || root.dataset.sbcReady === '1') return;
    var track = root.querySelector('[data-sbc-track]');
    if (!track) return;
    root.dataset.sbcReady = '1';

    var cards = [].slice.call(track.querySelectorAll('[data-sbc-card]'));
    var prev = root.querySelector('[data-sbc-prev]');
    var next = root.querySelector('[data-sbc-next]');
    var fill = root.querySelector('[data-sbc-fill]');
    var tabsWrap = root.querySelector('[data-sbc-tabs]');
    var tabs = [].slice.call(root.querySelectorAll('[data-sbc-tab]'));
    var motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var frame = 0;
    var settleTimer = 0;
    var focusCard = null;

    // Card start positions honour the track's scroll-padding (the mobile gutter).
    function targetOf(card) {
      var pad = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
      return Math.max(0, card.offsetLeft - pad);
    }
    function maxScroll() { return Math.max(0, track.scrollWidth - track.clientWidth); }
    function clampLeft(left) { return Math.max(0, Math.min(left, maxScroll())); }

    /* ---- adaptive height ------------------------------------------------- */
    // Cards at least 50% inside the track viewport for a given scroll position
    // (offset maths, so it also answers for a scroll that has not happened yet).
    function cardsInView(left) {
      var width = track.clientWidth;
      return cards.filter(function (card) {
        var size = card.offsetWidth;
        var start = card.offsetLeft - left;
        var shown = Math.min(start + size, width) - Math.max(start, 0);
        return size > 0 && shown >= size * 0.5;
      });
    }

    function fitHeight(destination, instant) {
      var inView = cardsInView(track.scrollLeft);
      if (destination != null) inView = inView.concat(cardsInView(destination));
      if (focusCard && track.contains(focusCard)) inView.push(focusCard);
      if (!inView.length) return;

      var tallest = 0;
      inView.forEach(function (card) { tallest = Math.max(tallest, card.offsetHeight); });
      var height = tallest + 'px';
      if (track.style.height !== height) {
        if (instant) {
          track.style.transition = 'none';
          track.style.height = height;
          void track.offsetHeight; // commit before the transition comes back
          track.style.transition = '';
        } else {
          track.style.height = height;
        }
      }
      // overflow-y clips; never leave the rail scrolled vertically.
      if (track.scrollTop) track.scrollTop = 0;
    }

    /* ---- controls, progress, tabs ---------------------------------------- */
    function go(left) {
      var destination = clampLeft(left);
      // Grow to cover both the cards leaving and the cards arriving; the
      // scrollend pass shrinks to the arriving cards only.
      fitHeight(destination, false);
      track.scrollTo({ left: destination, behavior: motion.matches ? 'auto' : 'smooth' });
    }

    function activeIndex() {
      var x = track.scrollLeft, best = 0, dist = Infinity;
      cards.forEach(function (card, i) {
        var d = Math.abs(targetOf(card) - x);
        if (d < dist) { dist = d; best = i; }
      });
      return best;
    }

    function sync() {
      frame = 0;
      var max = maxScroll();
      var x = track.scrollLeft;
      var scrollable = max > 1;
      var focused = document.activeElement;

      if (prev) { prev.hidden = !scrollable; prev.disabled = x <= 1; }
      if (next) { next.hidden = !scrollable; next.disabled = x >= max - 1; }
      // A control that just disabled itself would drop focus to <body>.
      if (focused === next && next.disabled && prev && !prev.disabled) prev.focus();
      if (focused === prev && prev.disabled && next && !next.disabled) next.focus();

      if (fill && track.scrollWidth > 0) {
        var visible = Math.min(1, track.clientWidth / track.scrollWidth);
        var ratio = scrollable ? x / max : 0;
        fill.style.width = visible * 100 + '%';
        fill.style.transform = 'translateX(' + ratio * (1 / visible - 1) * 100 + '%)';
      }

      var active = activeIndex();
      tabs.forEach(function (tab, i) { tab.setAttribute('aria-current', i === active ? 'true' : 'false'); });
    }
    function schedule() { if (!frame) frame = window.requestAnimationFrame(sync); }
    function settled() { fitHeight(null, false); }

    if (prev) {
      prev.addEventListener('click', function () {
        var x = track.scrollLeft;
        for (var i = cards.length - 1; i >= 0; i--) {
          var t = targetOf(cards[i]);
          if (t < x - 2) { go(t); return; }
        }
        go(0);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        var x = track.scrollLeft;
        for (var i = 0; i < cards.length; i++) {
          var t = targetOf(cards[i]);
          if (t > x + 2) { go(t); return; }
        }
        go(maxScroll());
      });
    }
    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { if (cards[i]) go(targetOf(cards[i])); });
    });
    if (tabsWrap && tabs.length) tabsWrap.hidden = false;

    track.addEventListener('scroll', schedule, { passive: true });
    if ('onscrollend' in window) {
      track.addEventListener('scrollend', settled);
    } else {
      track.addEventListener('scroll', function () {
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(settled, SETTLE_MS);
      }, { passive: true });
    }

    // Focus inside a card: that card is always fully shown, without animating.
    track.addEventListener('focusin', function (event) {
      var card = event.target.closest && event.target.closest('[data-sbc-card]');
      if (!card) return;
      focusCard = card;
      fitHeight(null, true);
    });
    track.addEventListener('focusout', function (event) {
      if (event.relatedTarget && track.contains(event.relatedTarget)) return;
      focusCard = null;
      fitHeight(null, false);
    });

    window.addEventListener('resize', function () {
      schedule();
      fitHeight(null, true);
    });
    // Web fonts and late images change card heights after first paint.
    if ('ResizeObserver' in window) {
      var observer = new ResizeObserver(function () { fitHeight(null, true); });
      cards.forEach(function (card) { observer.observe(card); });
    }
    window.addEventListener('load', function () { fitHeight(null, true); });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { fitHeight(null, true); });

    sync();
    fitHeight(null, true);
  }

  function initAll(scope) {
    [].forEach.call((scope || document).querySelectorAll(SELECTOR), init);
  }

  initAll();
  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
})();
